import { useMutation } from '@apollo/client';
import {
  AlertService,
  Button,
  Snackbar,
  TextInput,
  Timing,
  Theme as theme,
  ShipmentCashIcon,
  TruckDoorOpenIcon,
  Radio,
} from '@spoiler-alert/ui-library';
import accounting from 'accounting';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { createUseStyles } from 'react-jss';
import truckType from '../../enums/truck-type';
import INDUSTRY_STANDARDS from '../../enums/truck-load-industry-standards';
import { updateTruckTypeCosts, updateTruckMaximums } from '../../graphql/mutations';
import StepContainer from './components/step-container';
import InputContainer from '../../components/inputs/input-container';

const useStyles = createUseStyles({
  wrapper: {
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  },
  title: {
    fontSize: 16,
    lineHeight: 1.5,
    padding: 0,
    margin: '24px 24px -8px 24px !important',
  },
  subtitle: {
    fontSize: 12,
    marginBottom: '12px',
  },
  previewDescription: {
    fontSize: 14,
    color: theme.grey,
    margin: '4px 0px',
  },
  description: {
    fontSize: 14,
    margin: 0,
    lineHeight: 1.43,
    letterSpacing: '-0.08px',
  },
  verticalContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '200px',
    margin: 'auto',
    '&:first-child': {
      marginRight: 'auto',
      marginLeft: 0,
    },
    '&:last-child': {
      marginLeft: 'auto',
      marginRight: 0,
    },
  },
  radioContainer: {
    fontSize: 16,
    fontWeight: 400,
    letterSpacing: '-0.176px',
  },
  container: {
    display: 'flex',
  },
  previewContainer: {
    display: 'flex',
  },
  saveSettings: {
    display: 'flex',
    justifyContent: 'flex-end',
    boxShadow: 'inset 0px 1px 0 0 #d9dee1',
    padding: '24px',
    width: '100%',
    '& button': {
      marginLeft: 8,
    },
  },
  // shared with input-card.js
  row: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  titleMax: {
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '-0.176px',
    margin: 0,
  },
  subtitleMax: {
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '20px',
    letterSpacing: '-0.08px',
    textAlign: 'left',
    margin: [0, 0, 16, 0],
  },
  subtitleDescription: {
    fontSize: '12px',
    lineHeight: 1.33,
    fontWeight: 500,
    letterSpacing: 'normal',
    textAlign: 'left',
    marginBottom: 12,
  },
  infoRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'start',
    fontSize: 14,
    fontWeight: 400,
    color: theme.grey,
    lineHeight: '16px',
  },
  infoRowTitle: {
    extend: 'infoRow',
    fontSize: 12,
    fontWeight: 500,
    color: theme.grey,
  },
  infoRowCell: {
    flex: '0 0 150px',
  },
  disclosure: {
    fontSize: 12,
    color: theme.grey80,
    fontWeight: 400,
    lineHeight: '16px',
    marginTop: 16,
  },
  standardsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginTop: 24,
  },
  emphasis: {
    fontStyle: 'italic',
    fontWeight: 500,
  },
});

const initializeStateFromDB = (truckTypeCosts) => {
  if (!truckTypeCosts) {
    return Object.values(truckType).map((truck) => ({ title: truck, fixedCost: 0, costPerMile: 0 }));
  }
  const { dry, refrigerated, frozen } = truckTypeCosts;
  return [
    {
      title: truckType.dry,
      fixedCost: dry?.fixedCost || 0,
      costPerMile: dry?.costPerMile || 0,
    },
    {
      title: truckType.refrigerated,
      fixedCost: refrigerated?.fixedCost || 0,
      costPerMile: refrigerated?.costPerMile || 0,
    },
    {
      title: truckType.frozen,
      fixedCost: frozen?.fixedCost || 0,
      costPerMile: frozen?.costPerMile || 0,
    },
  ];
};

const TRUCK_TYPES = ['dry', 'refrigerated', 'frozen'];

const MAX_LOAD_OPTIONS = [
  { text: 'None', value: 'none' },
  { text: 'Weight', value: 'weight' },
  { text: 'Pallets', value: 'pallets' },
];

const hasAnyValue = (bucket) => Boolean(bucket && (bucket.dry || bucket.refrigerated || bucket.frozen));

// Local edit state holds mode plus both weight and pallets so entered values stay sticky while toggling modes.
const initializeMaximumsFromDB = (truckMaximums) => {
  const weight = {
    dry: truckMaximums?.weight?.dry || 0,
    refrigerated: truckMaximums?.weight?.refrigerated || 0,
    frozen: truckMaximums?.weight?.frozen || 0,
  };
  const pallets = {
    dry: truckMaximums?.pallets?.dry || 0,
    refrigerated: truckMaximums?.pallets?.refrigerated || 0,
    frozen: truckMaximums?.pallets?.frozen || 0,
  };
  // Prefer the persisted mode; fall back to inferring from populated buckets for records saved before `mode` existed.
  const mode = truckMaximums?.mode || (hasAnyValue(weight) ? 'weight' : hasAnyValue(pallets) ? 'pallets' : 'none');
  return { mode, weight, pallets };
};

const optionForMode = (mode) => MAX_LOAD_OPTIONS.find((option) => option.value === mode) || MAX_LOAD_OPTIONS[0];

const DeliveryCostSettings = ({ user, saveText }) => {
  const classes = useStyles();
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [touched, setTouched] = useState(false);
  const [truckTypes, setTruckTypes] = useState(initializeStateFromDB(user.site.truckTypeCosts));
  const [maximums, setMaximums] = useState(() => initializeMaximumsFromDB(user.site.truckMaximums));
  const [backendData, setBackendData] = useState({});
  const [selectedTruckLimit, setSelectedTruckLimit] = useState(() => optionForMode(maximums.mode));
  const [updateCosts, { loading }] = useMutation(updateTruckTypeCosts);
  const [updateMaximums, { loading: savingMaximums }] = useMutation(updateTruckMaximums);

  const buildBackendData = (updatedCost) => {
    setBackendData({
      ...backendData,
      [updatedCost.title.toLowerCase()]: {
        fixedCost: updatedCost.fixedCost,
        costPerMile: updatedCost.costPerMile,
      },
    });
  };

  const updateTruckTypeCostsData = Timing.debounce((title, value, type) => {
    const updatedList = [...truckTypes];
    const foundType = updatedList.find((listItem) => title === listItem.title);
    foundType[type] = accounting.unformat(value);
    setTruckTypes(updatedList);
    buildBackendData(foundType);
  }, 10);

  const truckTypeCosts = () => {
    return truckTypes.map((type) => {
      return (
        <div key={type.title} className={classes.verticalContainer}>
          <h2 className={classes.subtitle}>{type.title}</h2>
          <TextInput
            type="text"
            value={accounting.formatMoney(type.fixedCost)}
            labelText="Fixed Delivery Cost"
            onChange={() => setTouched(true)}
            onBlur={(value) => updateTruckTypeCostsData(type.title, value, 'fixedCost')}
          />
          <TextInput
            type="text"
            value={accounting.formatMoney(type.costPerMile)}
            labelText="Cost Per Mile"
            onChange={() => setTouched(true)}
            onBlur={(value) => updateTruckTypeCostsData(type.title, value, 'costPerMile')}
          />
        </div>
      );
    });
  };

  const ratePreview = () => {
    const calculationAmounts = [50, 500, 1000, 1500, 3000];
    const tableInfo = (
      <div key="info-preview" className={classes.verticalContainer}>
        <span className={classes.infoRowTitle}>Distance</span>
        {calculationAmounts.map((amount) => (
          <span key={`${amount}-info`} className={classes.previewDescription}>
            {accounting.formatNumber(amount)} miles
          </span>
        ))}
      </div>
    );
    return [
      tableInfo,
      ...truckTypes.map((type) => {
        return (
          <div key={`${type.title}-preview`} className={classes.verticalContainer}>
            <span className={classes.infoRowTitle}>{type.title}</span>
            {calculationAmounts.map((amount) => (
              <span key={`${type.title}-${amount}`} className={classes.previewDescription}>
                {accounting.formatMoney(type.costPerMile * amount + type.fixedCost)}
              </span>
            ))}
          </div>
        );
      }),
    ];
  };
  const setMaximum = (mode, type, value) => {
    setMaximums((prev) => ({
      ...prev,
      [mode]: { ...prev[mode], [type]: accounting.unformat(value) },
    }));
  };

  const handleTruckLoadChange = (selectedItem) => {
    setSelectedTruckLimit(selectedItem);
    setTouched(true);
  };

  const industryStandards = (mode) => {
    const unitLabel = mode === 'weight' ? 'Max Weight (lbs)' : 'Max Pallets';
    return (
      <div className={classes.standardsContainer}>
        <div className={classes.subtitleMax}>Industry Standards</div>
        <div className={classes.infoRowTitle}>
          <div className={classes.infoRowCell}>Trailer Type</div>
          <div className={classes.infoRowCell}>{unitLabel}</div>
        </div>
        {TRUCK_TYPES.map((type) => (
          <div key={type} className={classes.infoRow}>
            <div className={classes.infoRowCell}>{truckType[type]}</div>
            <div className={classes.infoRowCell}>{INDUSTRY_STANDARDS[type][mode]}</div>
          </div>
        ))}
        <div className={classes.disclosure}>
          • Figures are industry rules of thumb, not regulatory limits; actual thresholds vary by carrier, lane, and freight density.
          <br />• Weight limits reflect standard 80,000 lb gross vehicle weight limit minus tractor/trailer tare weight.
          <br />• FTL breakeven thresholds are cost-efficiency guidelines — below these levels, LTL is typically more economical.
          <br />• Sources: ATS Inc. (Dry Van vs. Reefer comparison), Mercer Transportation (pallet capacity by trailer type), Honest Freight USA (FTL
          weight/size limits).
        </div>
      </div>
    );
  };

  const maximumInputs = (mode, heading) => (
    <div className={classes.row}>
      <hr />
      <h2 className={classes.titleMax}>{heading}</h2>
      <h2 className={classes.subtitleMax}>Delivery & pickup truck capacity</h2>
      <div className={classes.subtitleDescription}>Truck type</div>
      <InputContainer
        values={maximums[mode]}
        format={mode === 'weight' ? 'weight' : 'number'}
        setTouched={setTouched}
        onChange={(type, value) => setMaximum(mode, type, value)}
      />
      {industryStandards(mode)}
    </div>
  );

  const maxTruckLoadSettings = () => {
    return (
      <div className={classes.radioContainer}>
        <Radio name="max-truck-load" options={MAX_LOAD_OPTIONS} selectedItem={selectedTruckLimit} onChange={handleTruckLoadChange} />
        {selectedTruckLimit.value === 'weight' && maximumInputs('weight', 'Weight')}
        {selectedTruckLimit.value === 'pallets' && maximumInputs('pallets', 'Pallets')}
      </div>
    );
  };

  // Always send mode plus both buckets; the unselected bucket resets to 0 (None → both reset).
  const buildMaximumsPayload = () => {
    const mode = selectedTruckLimit.value;
    return {
      mode,
      weight: mode === 'weight' ? maximums.weight : { dry: 0, refrigerated: 0, frozen: 0 },
      pallets: mode === 'pallets' ? maximums.pallets : { dry: 0, refrigerated: 0, frozen: 0 },
    };
  };

  const save = () => {
    Promise.all([updateCosts({ variables: { truckTypeCosts: backendData } }), updateMaximums({ variables: { maximums: buildMaximumsPayload() } })])
      .then(([costRes, maxRes]) => {
        const costErrors = costRes.data.updateTrucktypeCosts.errors;
        const maxErrors = maxRes.data.updateTruckMaximums.errors;
        if (costErrors && costErrors.length > 0) {
          throw new Error(costErrors[0].message);
        }
        if (maxErrors && maxErrors.length > 0) {
          throw new Error(maxErrors[0].message);
        }
        setShowSuccessMessage(true);
        setTouched(false);
        setBackendData({});
      })
      .catch((err) => {
        AlertService.alert({
          type: 'warning',
          message: err.message,
        });
      });
  };

  const hideSuccessMessage = () => setShowSuccessMessage(false);

  return (
    <>
      <h1 className={classes.title}>Truck Rules</h1>
      <div className={classes.wrapper}>
        <StepContainer
          icon={() => <ShipmentCashIcon style={{ minWidth: 24, height: 24 }} />}
          title="Estimate Delivery Costs"
          description="Use our calculator to get an idea of what your delivery costs will be for each truck type. These estimates will be displayed during awarding to help guide your decisions."
        >
          <div className={classes.container}>{truckTypeCosts()}</div>
          <h2 className={classes.description}>Rate Preview</h2>
          <div className={classes.previewContainer}>{ratePreview()}</div>
        </StepContainer>
        <StepContainer
          icon={() => <TruckDoorOpenIcon style={{ minWidth: 24, height: 24 }} />}
          title="Truck Capacity"
          description={
            <>
              Set how much a single truck holds, by weight or pallets. We use this information to estimate truck counts per truck lane in the awarding
              workflow. <em className={classes.emphasis}>Smart Awarding doesn't use this value.</em>
            </>
          }
        >
          {maxTruckLoadSettings()}
        </StepContainer>
      </div>
      <div className={classes.saveSettings}>
        <Button onClick={save} primary disabled={!touched} loading={loading || savingMaximums} loadingText="Saving">
          {saveText || 'Save'}
        </Button>
      </div>
      <Snackbar show={showSuccessMessage} callback={hideSuccessMessage} message="Truck Rules saved successfully" />
    </>
  );
};

DeliveryCostSettings.propTypes = {
  user: PropTypes.object,
  saveText: PropTypes.string,
};

export default DeliveryCostSettings;
